import { invalidateNotificationQueries } from "@/lib/mobile-notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { NotificationIcon } from "@/components/notifications/NotificationIcon";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  apiGetNotifications,
  apiGetNotificationUnreadCount,
  apiMarkNotificationRead,
  apiMarkAllNotificationsRead,
  type AppNotification,
} from "@/lib/api";
import { getNotificationDisplayText, isMedicalCheckAssignmentNotification } from "@/lib/notification-display";
import { getTimeOffNotificationPath } from "@/lib/notification-routing";
import { formatDistanceToNow, type Locale } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useDateLocale } from "@/hooks/useDateLocale";

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string, locale: Locale) {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale });
  } catch {
    return "";
  }
}


// ── Notification item ─────────────────────────────────────────────────────────

function NotificationItem({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (
    id: number,
    entityId: number | null,
    entityType: string,
    notifType: string,
  ) => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const display = getNotificationDisplayText(notification, t);

  return (
    <button
      className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b last:border-b-0 ${
        !notification.isRead ? "bg-primary/5" : ""
      }`}
      onClick={() =>
        onRead(
          notification.id,
          notification.entityId ?? null,
          notification.entityType,
          notification.type,
        )
      }
    >
      <div className="flex items-start gap-2.5">
        <NotificationIcon type={notification.type} size="sm" dimmed={notification.isRead} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5">
            <p className="min-w-0 flex-1 text-sm font-medium whitespace-normal break-words [overflow-wrap:anywhere]">
              {display.title}
            </p>
            {!notification.isRead && (
              <span className="mt-1.5 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground whitespace-normal break-words [overflow-wrap:anywhere]">
            {display.message}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/60 whitespace-normal break-words [overflow-wrap:anywhere]">
            {timeAgo(notification.createdAt, dateLocale)}
          </p>
        </div>
      </div>
    </button>
  );
}

// ── Bell component ────────────────────────────────────────────────────────────

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdminOrHr = user?.role?.mainRole === "admin" || user?.role?.mainRole === "hr";
  const canAccessTeamLeave = isAdminOrHr || !!(user?.isLineManager || user?.role?.subRole === "line_manager");

  // Control popover open state so we can react to it
  const [open, setOpen] = useState(false);

  // Unread count — poll every 30s
  const { data: countData } = useQuery({
    queryKey: ["notificationCount"],
    queryFn: apiGetNotificationUnreadCount,
    refetchInterval: 30_000,
  });

  // Notification list — only runs while the popover is open; always fetches fresh
  // (staleTime: 0 ensures a refetch every time the popover opens or the count changes)
  const { data: notifData, refetch: refetchList } = useQuery({
    queryKey: ["notifications", 10],
    queryFn: () => apiGetNotifications(10, 0),
    enabled: open,
    staleTime: 0,
  });

  // When the unread count increases while the popover is open, refresh the list
  // so newly arrived notifications appear without any user action.
  const prevCount = useRef<number | undefined>(undefined);
  useEffect(() => {
    const count = countData?.count ?? 0;
    if (open && prevCount.current !== undefined && count > prevCount.current) {
      refetchList();
    }
    prevCount.current = count;
  }, [countData?.count, open, refetchList]);

  const markReadMutation = useMutation({
    mutationFn: (id: number) => apiMarkNotificationRead(id),
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });

  const markAllMutation = useMutation({
    mutationFn: apiMarkAllNotificationsRead,
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });

  function handleRead(
    id: number,
    entityId: number | null,
    entityType: string,
    notifType: string,
  ) {
    markReadMutation.mutate(id);
    setOpen(false);

    if (notifType === "salary_processed") {
      navigate("/my-salary");
    } else if (notifType === "salary_increase_approved" || notifType === "salary_increase_rejected") {
      navigate("/my-tasks?tab=submitted");
    } else if (notifType === "salary_increase_requested" || notifType === "workflow_task" || entityType === "workflow_task") {
      navigate(entityId ? `/my-tasks?taskId=${entityId}` : "/my-tasks");
    } else if (entityType === "workflow_instance") {
      // Owner-needed → open the Workflows page with the instance picker primed.
      // Owner-assigned → just open the instance detail.
      const claim = notifType === "workflow_owner_needed" ? "&claim=1" : "";
      navigate(`/workflows?tab=instances${entityId ? `&instanceId=${entityId}${claim}` : ""}`);
    } else if (notifType === "event_new") {
      navigate("/corporate-calendar");
    } else if (notifType === "team_join_request") {
      navigate("/teams-management?tab=requests");
    } else if (notifType === "team_request_approved" || notifType === "team_request_rejected") {
      navigate("/team-overview");
    } else if (entityType === "document") {
      navigate(`/documents${entityId ? `?openDocument=${entityId}` : ""}`);
    } else if (entityType === "time_off") {
      navigate(getTimeOffNotificationPath(notifType, entityId, canAccessTeamLeave));
    } else if (notifType === "onboarding_started" || entityType === "onboarding_session") {
      navigate(`/my-onboarding${entityId ? `?sessionId=${entityId}` : ""}`);
    } else if (entityType === "onboarding" || entityType === "messages") {
      navigate(`/messages${entityId ? `?with=${entityId}` : ""}`);
    } else if (entityType === "survey") {
      navigate("/my-surveys");
    } else if (entityType === "offboarding") {
      navigate(`/team-overview${entityId ? `?openOffboarding=${entityId}` : ""}`);
    } else if (entityType === "recruitment_application") {
      navigate(`/recruitment${entityId ? `?tab=applications&applicationId=${entityId}` : "?tab=applications"}`);
    } else if (entityType === "recruitment_position") {
      navigate(`/recruitment${entityId ? `?tab=positions&positionId=${entityId}` : "?tab=positions"}`);
    } else if (entityType === "expense") {
      if (notifType === "expense_submitted") {
        navigate(`/expenses${entityId ? `?expense=${entityId}` : ""}`);
      } else {
        navigate(`/my-expenses${entityId ? `?expense=${entityId}` : ""}`);
      }
    } else if (entityType === "performance") {
      navigate("/performance");
    } else if (entityType === "medical_check") {
      const assignmentLink = entityId && isMedicalCheckAssignmentNotification(notifType);
      const legacy = notifType === "medical_check_confirmed" ? "&legacyEmployeeId=true" : "";
      if (isAdminOrHr) {
        navigate(`/medical-checkups${assignmentLink ? `?assignment=${entityId}${legacy}` : ""}`);
      } else {
        navigate(`/${assignmentLink ? `?assignmentId=${entityId}` : ""}`);
      }
    } else if (entityType === "concern_case") {
      // concern_new / concern_assigned → HR goes to HR Cases page with the case open
      if (notifType === "concern_new" || notifType === "concern_assigned") {
        navigate(`/hr-cases${entityId ? `?caseId=${entityId}` : ""}`);
      } else {
        // concern_status_changed / concern_feedback → employee goes to Raise Concern my cases
        navigate(`/raise-concern${entityId ? `?caseId=${entityId}` : ""}`);
      }
    }
    // No fallback — unknown types simply close the popover without navigating
  }

  const unreadCount = countData?.count ?? 0;
  const notifications = notifData?.notifications ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
          <span className="sr-only">{t("notifications.title")}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[calc(100vw-1rem)] max-w-[360px] p-0"
        align="end"
        sideOffset={8}
        collisionPadding={8}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <h4 className="min-w-0 flex-1 text-sm font-semibold whitespace-normal break-words [overflow-wrap:anywhere]">
            {t("notifications.title")}
            {unreadCount > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                ({t("notifications.unread", { count: unreadCount })})
              </span>
            )}
          </h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto min-h-7 max-w-full shrink-0 whitespace-normal px-2 py-1 text-right text-xs leading-tight"
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
            >
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>

        {/* Notifications */}
        <ScrollArea className="h-[min(400px,calc(100vh-12rem))] min-h-32">
          {notifications.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {t("notifications.noNotifications")}
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onRead={handleRead}
              />
            ))
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="px-4 py-2 text-center">
              <Button
                variant="ghost"
                size="sm"
                className="h-auto min-h-8 w-full whitespace-normal break-words py-1.5 text-xs [overflow-wrap:anywhere]"
                onClick={() => navigate("/notifications")}
              >
                {t("notifications.viewAll", "View all notifications")}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
